import React, { useState } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { Security } from '@okta/okta-react';
import { OktaAuth } from '@okta/okta-auth-js';

import createStyles from '@guestyci/foundation/createStyles';
import { useToast } from '@guestyci/foundation/Toast';
import useTranslations from '@guestyci/localize/useTranslations';
import history from '@guestyci/history';

import Loader from './Loader';
import Layout from '../components/Layout';
import LoginCallback from '../components/LoginCallback/LoginCallback';
import config from '../okta-config';
import reportFullstory from '../utils/reportFullstory';

import background from '../assets/background.svg';
import saveUserData from '../utils/user';

import Login from '../login';
import Logout from '../logout';
import Forgot from '../forgot';
import {
  getWixRedirectUrl,
  getZendeskRedirectUrl,
} from '../utils/getRedirectUrl';

const oktaAuth = new OktaAuth(config.oidc);

export const useStyles = createStyles({
  root: {
    display: 'flex',
    minHeight: '100vh',
    background: `transparent url(${background}) no-repeat center bottom`,
    backgroundSize: 'cover',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const App = () => {
  const token = window.localStorage.getItem('token');
  const location = useLocation();
  const classes = useStyles();
  const [error, setError] = useState(null);
  const [loadingText] = useTranslations([
    { id: 'auth_page:layout.loading', d: 'Loading...' },
  ]);

  const [redirectingText] = useTranslations([
    { id: 'auth_page:layout.redirecting', d: 'Logged in, redirecting...' },
  ]);

  const { addToast } = useToast();

  const redirectUrl = async ({ wixRedirectUrl, zendeskRedirectUrl }) => {
    if (wixRedirectUrl) {
      try {
        const accessToken = await oktaAuth.tokenManager.get('accessToken');
        const redirectURL = await getWixRedirectUrl({
          accessToken,
          wixRedirectUrl,
        });
        window.sessionStorage.clear();
        window.location.replace(redirectURL);
      } catch (e) {
        console.log(e);
        history.push('/login');
        setError(
          'Something wrong with wix login. Please try again or contact support'
        );
      }
    } else if (zendeskRedirectUrl) {
      try {
        const accessToken = await oktaAuth.tokenManager.get('accessToken');
        const redirectURL = await getZendeskRedirectUrl({
          accessToken,
          zendeskRedirectUrl,
        });
        window.sessionStorage.clear();
        window.location.replace(redirectURL);
      } catch (e) {
        console.log(e);
        history.push('/login');
        setError(
          'Something wrong with zendesk login. Please try again or contact support'
        );
      }
    }
  };

  const restoreOriginalUri = async () => {
    console.log('RESTORING ORIGINAL URL');
    const wixRedirectUrl = window.sessionStorage.getItem('wixRedirectURL');
    const zendeskRedirectUrl = window.sessionStorage.getItem(
      'zendeskRedirectUrl'
    );

    if (wixRedirectUrl || zendeskRedirectUrl) {
      redirectUrl({ wixRedirectUrl, zendeskRedirectUrl });
    } else {
      const ref = window.sessionStorage.getItem('ref');
      window.sessionStorage.clear();
      console.log('Going to ref', { ref });
      window.location.replace(ref || '/');
    }
  };

  const customAuthHandler = () => {
    console.log('CUSTOMING AUTH HANDLER');
    history.push('/login');
  };

  const onAuthResume = async () => {
    console.log('RESUMING');
    history.push('/login');
  };

  const {
    redirectUrl: wixRedirectUrl,
    return_to: zendeskRedirectUrl,
  } = history.getQueryParams();
  const isWixLogin = !!wixRedirectUrl;
  const isZendeskLogin = !!zendeskRedirectUrl;

  const shouldRedirect =
    !isZendeskLogin &&
    !isWixLogin &&
    token &&
    process.env.NODE_ENV !== 'development';

  if (shouldRedirect && location.pathname !== '/logout') {
    window.location.replace('/');
    return null;
  }

  if (error) {
    addToast.danger(error);
  }

  const handleOnTokenRecieved = async () => {
    try {
      const accessToken = await oktaAuth.tokenManager.get('accessToken');
      const eventData = {
        nowMinusIat: Date.now() / 1000 - accessToken.claims.iat,
        userId: accessToken.claims.userId,
        accountId: accessToken.claims.accountId,
        email: accessToken.claims.sub,
        name: accessToken.claims.fullName,
      };

      if (accessToken) {
        saveUserData(accessToken.accessToken);
      }

      if (60 * 1 < Math.abs(eventData.nowMinusIat)) {
        reportFullstory('authentication:ubnormalClockMargin', eventData);
      }
    } catch (logError) {
      console.log(logError);
    }
  };

  return (
    <div className={classes.root}>
      <Security
        oktaAuth={oktaAuth}
        onAuthRequired={customAuthHandler}
        restoreOriginalUri={restoreOriginalUri}
      >
        <Switch>
          <Route exact path="/login" component={Login} />
          <Route
            path="/login/callback"
            render={props => {
              return (
                <LoginCallback
                  {...props}
                  oktaAuth={oktaAuth}
                  authState={oktaAuth.authStateManager.getAuthState()}
                  loadingElement={<Loader text={redirectingText} />}
                  onAuthResume={onAuthResume}
                  onTokenRecieved={handleOnTokenRecieved}
                />
              );
            }}
          />
          <Route path="/logout" component={Logout} />
          <Route>
            <Layout>
              <Switch>
                <Route path="/forgot" component={Forgot} />
                <Route>
                  <Redirect to={{ pathname: '/login' }} />
                </Route>
              </Switch>
            </Layout>
          </Route>
        </Switch>
      </Security>
    </div>
  );
};

export default App;
